import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { LEADERSHIP } from "../i18n/content";
import SectionHead from "../components/SectionHead";
import { Reveal } from "../components/Reveal";
import Counter from "../components/Counter";
import ConsultForm from "../components/ConsultForm";
import { Linkedin, Flame, Handshake, ShieldCheck, Lightbulb, Check, ArrowRight } from "lucide-react";

export default function About() {
  const { t, lang, dir } = useLang();
  const a = t.about;
  const ar = lang === "ar";
  const flip = dir === "rtl" ? "scaleX(-1)" : "none";
  const valIcons = [Flame, Handshake, ShieldCheck, Lightbulb];

  const mission = ar
    ? "نساعد المؤسسات على التحوّل عبر الأتمتة الذكية والذكاء الاصطناعي والحلول السحابية الآمنة."
    : "We help enterprises transform through intelligent automation, AI and secure cloud solutions.";
  const heroPoints = ar
    ? ["الذكاء الاصطناعي وأتمتة العمليات للمؤسسات", "تنفيذ آمن ومتوافق مع الأنظمة", "خبرة إقليمية بمعايير عالمية"]
    : ["Enterprise AI & process automation", "Secure, compliant delivery", "Regional expertise, global standards"];
  const benefits = ar
    ? ["استشارة مجانية بالكامل", "خارطة طريق مخصّصة لأهدافك", "خبرة إقليمية عميقة", "تنفيذ سريع وقابل للقياس"]
    : ["A completely free consultation", "A roadmap tailored to your goals", "Deep regional expertise", "Fast, measurable delivery"];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-16 md:pt-36 md:pb-24">
        <div className="absolute inset-0 rq-grid-bg opacity-40" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 75% 15%, rgba(20,162,75,0.14), transparent 55%)" }} />
        <div className="rq-container relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="rq-tag" data-testid="about-hero-tag">{a.hero.tag}</span>
              <h1 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-white" data-testid="about-hero-title">
                {a.hero.title}
              </h1>
              <p className="mt-6 max-w-xl text-[15px] md:text-lg leading-relaxed text-[color:var(--rq-muted)]">{a.hero.desc}</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact" className="rq-glow-btn px-7 py-3.5 inline-flex items-center gap-2" data-testid="about-hero-contact">
                  {ar ? "تواصل معنا" : "Get in touch"}
                  <ArrowRight size={16} style={{ transform: flip }} />
                </Link>
                <Link to="/why-us" className="rq-ghost-btn px-7 py-3.5 inline-flex items-center">{ar ? "لماذا نحن" : "Why us"}</Link>
              </div>
              <div className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-[rgba(14,122,58,0.3)] bg-[rgba(14,122,58,0.06)] px-4 py-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[color:var(--rq-green-bright)] opacity-70 animate-ping" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[color:var(--rq-green)]" />
                </span>
                <span className="text-xs font-semibold text-[color:var(--rq-green-2)]">
                  {ar ? "نخدم عملاءنا عبر السعودية ودول الخليج" : "Operating across KSA & the GCC"}
                </span>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={150}>
              <div className="relative">
                <div className="rq-card p-7 md:p-8 bg-white">
                  <span className="rq-tag" style={{ color: "var(--rq-green)" }}>{ar ? "مهمتنا" : "Our mission"}</span>
                  <p className="mt-3 text-[15px] md:text-base font-medium leading-relaxed text-[color:var(--rq-navy)]">{mission}</p>
                  <div className="mt-6 space-y-3">
                    {heroPoints.map((p, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="grid place-items-center h-7 w-7 rounded-full bg-[rgba(14,122,58,0.12)] text-[color:var(--rq-green)] shrink-0"><Check size={14} /></span>
                        <span className="text-sm text-[color:var(--rq-navy)]">{p}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rq-float absolute -z-10 -top-6 ltr:-right-6 rtl:-left-6 h-28 w-28 rounded-3xl bg-[rgba(14,122,58,0.1)] border border-[rgba(14,122,58,0.25)]" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* IMPACT STATS BAND */}
      <section className="py-14 md:py-16 rq-greendark">
        <div className="rq-container grid gap-6 sm:grid-cols-3">
          {t.stats.map((s, i) => (
            <Reveal key={i} delay={i * 120}>
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center" data-testid={`about-stat-${i}`}>
                <div className="font-display text-4xl md:text-5xl font-bold text-white" style={{ textShadow: "0 0 30px rgba(20,162,75,0.4)" }}>
                  <Counter value={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-2 font-semibold text-white">{s.label}</div>
                {s.sub && <div className="mt-1 text-sm text-[color:var(--rq-muted)]">{s.sub}</div>}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* STORY BLOCKS */}
      <section className="py-16 md:py-24 rq-light">
        <div className="rq-container space-y-16 md:space-y-24">
          {a.blocks.map((b, i) => {
            const rev = i % 2 === 1;
            return (
              <div key={i} className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
                <Reveal className={rev ? "md:order-2" : ""}>
                  <div className="relative">
                    <div className="absolute -inset-3 rounded-[32px] bg-[radial-gradient(circle_at_30%_20%,rgba(20,162,75,0.14),transparent_70%)]" />
                    <img src={b.img} alt={b.h} loading="lazy" className="relative w-full aspect-[4/3] object-cover rounded-3xl border border-[rgba(14,122,58,0.2)] shadow-[0_30px_60px_-30px_rgba(14,122,58,0.4)]" />
                  </div>
                </Reveal>
                <Reveal delay={120} className={rev ? "md:order-1" : ""}>
                  <div>
                    <span className="rq-tag">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="font-display mt-3 text-2xl md:text-3xl font-bold text-white leading-tight">{b.h}</h3>
                    <p className="mt-4 text-[color:var(--rq-muted)] leading-relaxed text-[15px] md:text-base">{b.p}</p>
                  </div>
                </Reveal>
              </div>
            );
          })}
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="py-16 md:py-20 relative">
        <div className="absolute inset-0 rq-grid-bg opacity-30" />
        <div className="rq-container relative">
          <SectionHead tag={a.valuesTag} title={a.valuesTitle} />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {a.values.map((v, i) => {
              const Icon = valIcons[i];
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="rq-card p-7 h-full group relative overflow-hidden" data-testid={`about-value-${i}`}>
                    <span className="absolute inset-x-0 top-0 h-[3px] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" style={{ background: "linear-gradient(90deg,#0b5e2e,#14a24b)" }} />
                    <div className="grid place-items-center h-14 w-14 rounded-xl bg-[rgba(14,122,58,0.1)] text-[color:var(--rq-green)]">
                      {Icon && <Icon size={26} />}
                    </div>
                    <h3 className="font-display mt-5 text-lg font-semibold text-white">{v.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[color:var(--rq-muted)]">{v.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="py-16 md:py-24 rq-light">
        <div className="rq-container">
          <SectionHead tag={a.hero.tag} title={a.leadershipTitle} desc={a.leadershipDesc} />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {LEADERSHIP.map((m, i) => (
              <Reveal key={m.name} delay={(i % 4) * 80}>
                <div className="group" data-testid={`leadership-card-${i}`}>
                  <div className="relative overflow-hidden rounded-2xl bg-[#eef3f8] aspect-[4/5]">
                    <img src={m.img} alt={m.name} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[rgba(7,43,24,0.9)] via-[rgba(7,43,24,0.15)] to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-4">
                      <h4 className="font-display text-base font-bold text-[#ffffff]">{m.name}</h4>
                      <p className="mt-0.5 text-xs font-semibold text-[color:var(--rq-green-bright)]">{ar ? m.roleAr : m.role}</p>
                    </div>
                    {m.li && (
                      <a href={m.li} target="_blank" rel="noopener noreferrer" className="absolute top-3 ltr:right-3 rtl:left-3 grid place-items-center h-9 w-9 rounded-lg bg-white/90 text-[color:var(--rq-green)] opacity-0 translate-y-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0 hover:bg-white" aria-label={`${m.name} LinkedIn`}>
                        <Linkedin size={18} />
                      </a>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONSULTATION CTA */}
      <section className="py-16 md:py-24 rq-dark">
        <div className="rq-container grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="rq-tag">{t.ctaSection.tag}</span>
              <h2 className="font-display mt-4 text-3xl md:text-4xl font-bold text-white leading-tight">{a.collabTitle}</h2>
              <p className="mt-6 text-[color:var(--rq-muted)] leading-relaxed">{a.collabDesc}</p>
              <div className="mt-8 space-y-3">
                {benefits.map((b, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="grid place-items-center h-7 w-7 rounded-full bg-[rgba(20,162,75,0.2)] text-[color:var(--rq-green-bright)] shrink-0"><Check size={14} /></span>
                    <span className="text-white/90">{b}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={140}>
              <ConsultForm source="About Page" />
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
